import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiFileText as FileText, FiDatabase as Database, FiSearch as Search, FiPlus as Plus, FiRefreshCw as RefreshCw, FiClock as Clock,
  FiChevronRight as ChevronRight, FiAlertTriangle as AlertTriangle, FiCheckCircle as CheckCircle, FiHash as Hash,
  FiActivity as Activity, FiCopy as Copy, FiTrash2 as Trash2, FiCloud as Cloud, FiGitMerge as GitMerge,
  FiSquare as Square, FiCheckSquare as CheckSquare, FiCode, FiZap, FiMessageSquare
} from 'react-icons/fi';
import { getPrompts, getBedrockMappings, listEnvironments, uploadDatasetFile, previewImport, assignPromptProject, assignGenAIOpsDatasetProject, assignPromptWorkload, assignGenAIOpsDatasetWorkload } from '../../../../services/aiOperationsApi';
import { listDatasets, createDataset, deleteDataset, cloneDataset } from '../../../../services/datasetsApi';
import VectorExportsContent from '../components/vector-exports/VectorExportsContent';
import ImportFromBedrockModal from '../components/prompts/ImportFromBedrockModal';
import BedrockReconciliationReport from '../components/prompts/BedrockReconciliationReport';
import { BedrockSyncIndicator } from '../components/prompts/BedrockSyncBadge';
import { ExternalChangesBanner, useExternalChanges } from '../components/prompts/ExternalChangesNotification';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { useProject } from '../../../../contexts/ProjectContext';
import { useWorkloads } from '../../../../features/hub/workloads/hooks/useWorkloads';

// Tab configuration
const TABS = [
  { id: 'prompts', label: 'Prompts', icon: FileText },
  { id: 'datasets', label: 'Datasets', icon: Database },
  { id: 'vector-exports', label: 'Vector Exports', icon: Cloud }
];

// Types
interface PromptItem {
  id: string;
  project_id?: string | null;
  workload_id?: string | null;
  name: string;
  description?: string;
  environment?: string;
  version_number?: number;
  is_active?: boolean;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
  content_type?: string;
  [key: string]: unknown;
}

interface BedrockMappingData {
  aivilo_prompt_id?: string;
  bedrock_prompt_arn?: string;
  sync_status?: string;
  [key: string]: unknown;
}

interface DatasetItem {
  id: string;
  project_id?: string | null;
  workload_id?: string | null;
  name: string;
  description?: string;
  content_type?: string;
  test_case_count?: number;
  item_count?: number;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface ImportPreviewData {
  total_rows?: number;
  detected_content_type?: string;
  columns?: string[];
  [key: string]: unknown;
}

interface ConfirmDialogState {
  open: boolean;
  title?: string;
  message?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm?: (() => void | Promise<void>) | null;
}

// Prompts Tab Content
const PromptsContent = ({ onPromptClick, onCreatePrompt }: { onPromptClick: (id: string) => void; onCreatePrompt: () => void }) => {
  const { projects } = useProject();
  const { workloads } = useWorkloads();
  const [prompts, setPrompts] = useState<PromptItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReconciliationReport, setShowReconciliationReport] = useState(false);
  const [bedrockMappings, setBedrockMappings] = useState<Record<string, BedrockMappingData>>({});
  const [environments, setEnvironments] = useState(['development', 'staging', 'production']);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { count: externalChangesCount, refresh: refreshExternalChanges } = useExternalChanges();

  useEffect(() => {
    loadPrompts();
    loadBedrockMappings();
    loadEnvironments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentFilter]);

  // Load Bedrock mappings to show sync status
  const loadBedrockMappings = async () => {
    try {
      const response = await getBedrockMappings();
      // Create a map of aivilo_prompt_id -> mapping
      const mappingsMap = {};
      (response.mappings || []).forEach((m: BedrockMappingData) => {
        if (m.aivilo_prompt_id) {
          (mappingsMap as Record<string, BedrockMappingData>)[m.aivilo_prompt_id] = m;
        }
      });
      setBedrockMappings(mappingsMap);
    } catch (err: unknown) {
      console.error('Error loading Bedrock mappings:', err);
    }
  };

  const loadEnvironments = async () => {
    try {
      const data = await listEnvironments();
      if ((data.environments?.length ?? 0) > 0) {
        setEnvironments(data.environments);
      }
    } catch (err: unknown) {
      console.error('Error loading environments:', err);
    }
  };

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (environmentFilter) params.environment = environmentFilter;
      const data = await getPrompts(params);
      setPrompts(data.prompts || data.data || data || []);
    } catch (err: unknown) {
      console.error('Error loading prompts:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrompts = prompts.filter(prompt => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      prompt.name?.toLowerCase().includes(searchLower) ||
      prompt.description?.toLowerCase().includes(searchLower) ||
      prompt.id?.toLowerCase().includes(searchLower)
    );
  });

  // Group prompts by name+environment to show latest version per environment
  // This ensures production prompts appear with their correct environment tag
  const groupedPrompts = filteredPrompts.reduce((acc: Record<string, PromptItem>, prompt) => {
    const key = `${prompt.name}::${prompt.environment || 'development'}`;
    if (!acc[key] || (prompt.version_number || 0) > (acc[key].version_number || 0)) {
      acc[key] = prompt;
    }
    return acc;
  }, {});

  // Sort: production first, then staging, then development, then by name
  const envOrder: Record<string, number> = { production: 0, staging: 1, development: 2 };
  const promptsList = Object.values(groupedPrompts).sort((a, b) => {
    const envA = envOrder[a.environment || ''] ?? 3;
    const envB = envOrder[b.environment || ''] ?? 3;
    if (envA !== envB) return envA - envB;
    return a.name.localeCompare(b.name);
  });

  const getEnvironmentBadge = (env: string) => {
    const envConfig: Record<string, { bg: string; text: string; Icon: React.ComponentType<{ className?: string }> }> = {
      development: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300', Icon: FiCode },
      staging: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300', Icon: Activity },
      production: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300', Icon: FiZap }
    };

    const config = envConfig[env] ?? { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-800 dark:text-purple-300', Icon: Hash };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${config.bg} ${config.text}`}>
        <config.Icon className="w-3 h-3" />
        {env}
        {env === 'production' && (
          <CheckCircle className="w-3 h-3 ml-0.5" />
        )}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find((project) => project.id === projectId)?.name ?? null;
  };

  const getWorkloadName = (workloadId: string | null | undefined): string | null => {
    if (!workloadId) return null;
    return workloads.find((w) => w.id === workloadId)?.name ?? null;
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (
      prev.size === promptsList.length ? new Set() : new Set(promptsList.map((prompt) => prompt.id))
    ));
  }, [promptsList]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  // Calculate stats for summary cards
  const totalPrompts = prompts.length;
  const activePrompts = prompts.filter(p => p.is_active).length;
  const totalUsage = prompts.reduce((sum, p) => sum + (p.usage_count || 0), 0);

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Prompts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalPrompts)}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Prompts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(activePrompts)}
              </p>
            </div>
            <Activity className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Usage</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalUsage)}
              </p>
            </div>
            <Hash className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={toggleSelectAll}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={selectedIds.size === promptsList.length && promptsList.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === promptsList.length && promptsList.length > 0 ? (
              <CheckSquare className="w-5 h-5" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <select
            value={environmentFilter}
            onChange={(e) => setEnvironmentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Environments</option>
            {environments.map(env => (
              <option key={env} value={env}>
                {env.charAt(0).toUpperCase() + env.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={loadPrompts}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowReconciliationReport(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-green-300 dark:border-green-600 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            <GitMerge className="w-4 h-4" />
            Sync Status
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <Cloud className="w-4 h-4" />
            Import from Bedrock
          </button>
          <button
            onClick={onCreatePrompt}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Prompt
          </button>
        </div>
      </div>

      {/* External Changes Banner */}
      {externalChangesCount > 0 && (
        <ExternalChangesBanner
          count={externalChangesCount}
          onClick={() => setShowImportModal(true)}
          className="mb-6"
        />
      )}

      {selectedIds.size > 0 && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {selectedIds.size} prompt{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              onAssign={async (projectId) => {
                await Promise.all(Array.from(selectedIds).map((id) => assignPromptProject(id, projectId)));
                setPrompts((prev) => prev.map((prompt) => (
                  selectedIds.has(prompt.id) ? { ...prompt, project_id: projectId } : prompt
                )));
                setSelectedIds(new Set());
              }}
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                await Promise.all(Array.from(selectedIds).map((id) => assignPromptWorkload(id, workloadId)));
                setSelectedIds(new Set());
                loadPrompts();
              }}
              size="sm"
            />
          </div>
        </div>
      )}

      {/* Prompts List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {promptsList.length === 0 ? (
          <div className="p-12">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Create Your First Prompt
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Prompts are reusable templates that your agents fetch at runtime.
                Use variables like <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm">{'{{user_input}}'}</code> for dynamic content.
              </p>

              {/* Quick Start Guide */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Quick Start</h4>
                <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">1</span>
                    <span>Create a prompt with a descriptive name (e.g., "customer-support-v1")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">2</span>
                    <span>Add template variables for dynamic inputs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">3</span>
                    <span>Test with a dataset in the Evaluation tab</span>
                  </li>
                </ol>
              </div>

              <button
                onClick={onCreatePrompt}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <Plus className="w-5 h-5" />
                Create First Prompt
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {promptsList.map((prompt) => (
              <div
                key={prompt.id}
                onClick={() => onPromptClick(prompt.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPromptClick(prompt.id); }} role="button" tabIndex={0}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(prompt.id); }}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {selectedIds.has(prompt.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {prompt.name}
                      </span>
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded">
                        v{prompt.version_number}
                      </span>
                      {prompt.environment && getEnvironmentBadge(prompt.environment)}
                      {bedrockMappings[prompt.id] && (
                        <BedrockSyncIndicator mapping={bedrockMappings[prompt.id] as unknown as import('../components/prompts/BedrockSyncBadge').BedrockMapping} />
                      )}
                      {getProjectName(prompt.project_id) && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                          {getProjectName(prompt.project_id)}
                        </span>
                      )}
                      {getWorkloadName(prompt.workload_id) && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                          {getWorkloadName(prompt.workload_id)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {prompt.description && (
                        <span className="truncate max-w-md">{prompt.description}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(prompt.created_at || prompt.updated_at)}
                      </span>
                    </div>
                  </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import from Bedrock Modal */}
      <ImportFromBedrockModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={(_imported) => {
          loadPrompts();
          loadBedrockMappings();
          refreshExternalChanges();
        }}
      />

      {/* Bedrock Reconciliation Report */}
      <BedrockReconciliationReport
        isOpen={showReconciliationReport}
        onClose={() => setShowReconciliationReport(false)}
        onRefresh={() => {
          loadPrompts();
          loadBedrockMappings();
          refreshExternalChanges();
        }}
      />
    </div>
  );
};

// Dataset content type configuration
const CONTENT_TYPES = [
  {
    id: 'test_cases',
    label: 'Test Cases',
    description: 'Input/output pairs for LLM evaluations and testing',
    Icon: CheckCircle,
    examples: 'User queries, expected responses, assertions',
    sampleData: {
      name: "Billing inquiry test",
      input_variables: { query: "How do I update my payment method?" },
      expected_output: "You can update your payment method in Settings > Billing.",
      difficulty: "easy"
    }
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Text content for RAG applications',
    Icon: FileText,
    examples: 'FAQs, knowledge base articles, documentation',
    sampleData: {
      item_type: "document",
      name: "Payment FAQ",
      content: "To update your payment method, navigate to Settings > Billing...",
      type_specific_data: { source: "knowledge-base", category: "billing" }
    }
  },
  {
    id: 'qa_pairs',
    label: 'Q&A Pairs',
    description: 'Question-answer pairs for fine-tuning',
    Icon: FiMessageSquare,
    examples: 'Training data, instruction tuning samples',
    sampleData: {
      item_type: "qa_pair",
      content: "Q&A about billing",
      type_specific_data: {
        question: "How do I update my payment method?",
        answer: "Go to Settings > Billing to update your payment method."
      }
    }
  },
  {
    id: 'mixed',
    label: 'Mixed Content',
    description: 'Combination of different content types',
    Icon: GitMerge,
    examples: 'Various data types in one dataset',
    sampleData: {
      items: [
        { item_type: "document", content: "Product FAQ..." },
        { item_type: "qa_pair", content: "Q&A pair...", type_specific_data: { question: "...", answer: "..." } }
      ]
    }
  }
];

// Get content type badge for display
const getContentTypeBadge = (contentType: string) => {
  const type = CONTENT_TYPES.find(t => t.id === contentType) || CONTENT_TYPES[0];
  const colorMap = {
    test_cases: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300',
    documents: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
    qa_pairs: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
    mixed: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300'
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${colorMap[contentType as keyof typeof colorMap] || colorMap.test_cases}`}>
      <type.Icon className="w-3 h-3" />
      {type.label}
    </span>
  );
};

// Datasets Tab Content
const DatasetsContent = ({ onDatasetClick }: { onDatasetClick: (id: string) => void }) => {
  const { projects } = useProject();
  const { workloads } = useWorkloads();
  const [datasets, setDatasets] = useState<DatasetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDataset, setNewDataset] = useState({ name: '', description: '', tags: '', content_type: 'test_cases' });
  const [creating, setCreating] = useState(false);
  const [nameWarning, setNameWarning] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState('manual'); // 'manual' or 'import'
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreviewData | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ open: false, title: '', message: '', onConfirm: null, variant: 'info' });
  const [nameWarningConfirmed, setNameWarningConfirmed] = useState(false);
  const [showSampleFormat, setShowSampleFormat] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDatasets({ limit: 100 });
      setDatasets(data.data || data || []);
    } catch (err: unknown) {
      console.error('Error loading datasets:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  // Check if a dataset with the same name exists
  const checkDuplicateName = (name: string) => {
    const duplicate = datasets.find(d => d.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      setNameWarning(`A dataset named "${duplicate.name}" already exists. You can still create another one with the same name.`);
    } else {
      setNameWarning(null);
    }
  };

  const handleFileSelect = async (file: File) => {
    setImportFile(file);
    setImportPreview(null);
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('max_rows', '5');
      const preview = await previewImport(formData);
      setImportPreview(preview);
      // Auto-set content type based on detection
      if (preview.detected_content_type) {
        setNewDataset(prev => ({ ...prev, content_type: preview.detected_content_type }));
      }
    } catch (err: unknown) {
      console.error('Preview failed:', err);
    } finally {
      setPreviewing(false);
    }
  };

  const doCreateDataset = async () => {
    setCreating(true);
    try {
      if (dataSource === 'import' && importFile) {
        // Import from file
        const formData = new FormData();
        formData.append('file', importFile);
        formData.append('name', newDataset.name);
        formData.append('description', newDataset.description || '');
        formData.append('content_type', newDataset.content_type);
        const result = await uploadDatasetFile(formData);
        if (result.import_job?.status === 'failed') {
          throw new Error(result.import_job.error_message || 'Import failed');
        }
      } else {
        // Manual creation
        const tags = newDataset.tags?.split(',').map(t => t.trim()).filter(t => t) ?? [];
        await createDataset({
          name: newDataset.name,
          description: newDataset.description,
          tags,
          content_type: newDataset.content_type
        });
      }
      setShowCreateModal(false);
      setNewDataset({ name: '', description: '', tags: '', content_type: 'test_cases' });
      setNameWarning(null);
      setNameWarningConfirmed(false);
      setDataSource('manual');
      setImportFile(null);
      setImportPreview(null);
      loadDatasets();
      toast.success('Dataset created');
    } catch (err: unknown) {
      console.error('Error creating dataset:', err);
      toast.error((err instanceof Error ? err.message : String(err)) || 'Failed to create dataset');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDataset = async (e: React.FormEvent) => {
    e.preventDefault();

    // If there's a warning and user hasn't confirmed yet, show confirm dialog
    if (nameWarning && !nameWarningConfirmed) {
      setConfirmDialog({
        open: true,
        title: 'Duplicate Name',
        message: 'A dataset with this name already exists. Do you want to create it anyway?',
        variant: 'warning',
        onConfirm: () => {
          setConfirmDialog({ open: false });
          setNameWarningConfirmed(true);
          doCreateDataset();
        },
      });
      return;
    }

    doCreateDataset();
  };

  const handleCloneDataset = (e: React.MouseEvent, dataset: Record<string, any>) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Clone Dataset',
      message: `Clone dataset "${dataset.name}"?`,
      variant: 'info',
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        try {
          await cloneDataset(dataset.id, `${dataset.name} (Copy)`);
          loadDatasets();
          toast.success('Dataset cloned');
        } catch (err: unknown) {
          console.error('Error cloning dataset:', err);
          toast.error((err instanceof Error ? err.message : String(err)) || 'Failed to clone dataset');
        }
      },
    });
  };

  const handleDeleteDataset = (e: React.MouseEvent, dataset: Record<string, any>) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Delete Dataset',
      message: `Delete dataset "${dataset.name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        try {
          await deleteDataset(dataset.id);
          loadDatasets();
          toast.success('Dataset deleted');
        } catch (err: unknown) {
          console.error('Error deleting dataset:', err);
          toast.error((err instanceof Error ? err.message : String(err)) || 'Failed to delete dataset');
        }
      },
    });
  };

  const filteredDatasets = datasets.filter(dataset => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      dataset.name?.toLowerCase().includes(searchLower) ||
      dataset.description?.toLowerCase().includes(searchLower) ||
      dataset.id?.toLowerCase().includes(searchLower)
    );
  });

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find((project) => project.id === projectId)?.name ?? null;
  };

  const getWorkloadName = (workloadId: string | null | undefined): string | null => {
    if (!workloadId) return null;
    return workloads.find((w) => w.id === workloadId)?.name ?? null;
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => (
      prev.size === filteredDatasets.length ? new Set() : new Set(filteredDatasets.map((dataset) => dataset.id))
    ));
  }, [filteredDatasets]);

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString();
  };

  // Calculate stats for summary cards
  const totalDatasets = datasets.length;
  const totalItems = datasets.reduce((sum, d) => sum + (d.test_case_count || d.item_count || 0), 0);

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Datasets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalDatasets)}
              </p>
            </div>
            <Database className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Test Cases</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalItems)}
              </p>
            </div>
            <Hash className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={toggleSelectAll}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={selectedIds.size === filteredDatasets.length && filteredDatasets.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === filteredDatasets.length && filteredDatasets.length > 0 ? (
              <CheckSquare className="w-5 h-5" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search datasets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={loadDatasets}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Dataset
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {selectedIds.size} dataset{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              onAssign={async (projectId) => {
                await Promise.all(Array.from(selectedIds).map((id) => assignGenAIOpsDatasetProject(id, projectId)));
                setDatasets((prev) => prev.map((dataset) => (
                  selectedIds.has(dataset.id) ? { ...dataset, project_id: projectId } : dataset
                )));
                setSelectedIds(new Set());
              }}
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                await Promise.all(Array.from(selectedIds).map((id) => assignGenAIOpsDatasetWorkload(id, workloadId)));
                setSelectedIds(new Set());
                loadDatasets();
              }}
              size="sm"
            />
          </div>
        </div>
      )}

      {/* Datasets List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {filteredDatasets.length === 0 ? (
          <div className="p-12">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Database className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Create Your First Dataset
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Datasets contain test cases with input/output pairs to evaluate your prompts.
                Build a collection of scenarios to ensure consistent quality.
              </p>

              {/* Quick Start Guide */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">What to Include</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Input:</strong> The user query or context variables</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Expected Output:</strong> The ideal response for scoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Tags:</strong> Categories like "billing", "support", "edge-case"</span>
                  </li>
                </ul>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
              >
                <Plus className="w-5 h-5" />
                Create First Dataset
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredDatasets.map((dataset) => (
              <div
                key={dataset.id}
                onClick={() => onDatasetClick(dataset.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onDatasetClick(dataset.id); }} role="button" tabIndex={0}
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(dataset.id); }}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {selectedIds.has(dataset.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {dataset.name}
                      </span>
                      {dataset.content_type && getContentTypeBadge(dataset.content_type)}
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {dataset.test_case_count || dataset.item_count || 0} items
                      </span>
                      {getProjectName(dataset.project_id) && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                          {getProjectName(dataset.project_id)}
                        </span>
                      )}
                      {getWorkloadName(dataset.workload_id) && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                          {getWorkloadName(dataset.workload_id)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {dataset.description && (
                        <span className="truncate max-w-md">{dataset.description}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(dataset.created_at || dataset.updated_at)}
                      </span>
                    </div>
                  </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleCloneDataset(e, dataset)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Clone dataset"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteDataset(e, dataset)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete dataset"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Dataset Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create New Dataset</h2>
            <form onSubmit={handleCreateDataset}>
              <div className="space-y-4">
                {/* Content Type Selection */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content Type *
                  </span>
                  <div className="grid grid-cols-2 gap-3">
                    {CONTENT_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => { setNewDataset({ ...newDataset, content_type: type.id }); setShowSampleFormat(false); }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          newDataset.content_type === type.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <type.Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white">{type.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">{type.examples}</p>
                      </button>
                    ))}
                  </div>
                  {/* Sample format viewer */}
                  {(() => {
                    const selectedType = CONTENT_TYPES.find(t => t.id === newDataset.content_type);
                    if (!selectedType?.sampleData) return null;
                    return (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => setShowSampleFormat(!showSampleFormat)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {showSampleFormat ? 'Hide' : 'View'} sample format
                        </button>
                        {showSampleFormat && (
                          <pre className="mt-2 p-3 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto max-h-48">
                            {JSON.stringify(selectedType.sampleData, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Data Source */}
                <div>
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data Source
                  </span>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setDataSource('manual'); setImportFile(null); setImportPreview(null); }}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                        dataSource === 'manual'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-gray-900 dark:text-white">Manual</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add items one by one</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDataSource('import')}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${
                        dataSource === 'import'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-medium text-gray-900 dark:text-white">Import from File</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">JSON, JSONL, CSV</p>
                    </button>
                  </div>
                </div>

                {/* File Upload (shown when import selected) */}
                {dataSource === 'import' && (
                  <div>
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Upload File *
                    </span>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept=".json,.jsonl,.csv,.parquet"
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        className="hidden"
                        id="dataset-file-input"
                      />
                      <label htmlFor="dataset-file-input" className="cursor-pointer">
                        {importFile ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{importFile.name}</p>
                            <p className="text-xs text-gray-500 mt-1">{(importFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        ) : (
                          <div>
                            <Database className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">Click to select a file</p>
                            <p className="text-xs text-gray-400 mt-1">Supports JSON, JSONL, CSV, Parquet</p>
                          </div>
                        )}
                      </label>
                    </div>
                    {previewing && (
                      <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Analyzing file...
                      </p>
                    )}
                    {importPreview && (
                      <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          <strong>{importPreview.total_rows}</strong> rows detected
                          {importPreview.detected_content_type && (
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                              {importPreview.detected_content_type}
                            </span>
                          )}
                        </p>
                        {(importPreview.columns?.length ?? 0) > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Columns: {importPreview.columns!.join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input id="name"
                    type="text"
                    required
                    value={newDataset.name}
                    onChange={(e) => {
                      setNewDataset({ ...newDataset, name: e.target.value });
                      checkDuplicateName(e.target.value);
                    }}
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${
                      nameWarning ? 'border-yellow-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="e.g., Customer Support Scenarios"
                  />
                  {nameWarning && (
                    <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
                      {nameWarning}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea id="description"
                    value={newDataset.description}
                    onChange={(e) => setNewDataset({ ...newDataset, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Brief description of this dataset"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags-comma-separated" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input id="tags-comma-separated"
                    type="text"
                    value={newDataset.tags}
                    onChange={(e) => setNewDataset({ ...newDataset, tags: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., support, billing, english"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewDataset({ name: '', description: '', tags: '', content_type: 'test_cases' });
                    setNameWarning(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Create Dataset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onConfirm={confirmDialog.onConfirm || (() => setConfirmDialog({ open: false }))}
        onCancel={() => setConfirmDialog({ open: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
        variant={confirmDialog.variant || 'info'}
      />
    </div>
  );
};

const RegistryPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'prompts';

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const handlePromptClick = (promptId: string) => {
    navigate(`/ai-operations/prompts/${promptId}`);
  };

  const handleDatasetClick = (datasetId: string) => {
    navigate(`/ai-operations/datasets/${datasetId}`);
  };

  const handleCreatePrompt = () => {
    navigate('/ai-operations/prompts/new');
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Registry
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Manage prompts and datasets for your AI operations
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'prompts' && (
        <PromptsContent
          onPromptClick={handlePromptClick}
          onCreatePrompt={handleCreatePrompt}
        />
      )}
      {activeTab === 'datasets' && <DatasetsContent onDatasetClick={handleDatasetClick} />}
      {activeTab === 'vector-exports' && <VectorExportsContent />}
    </div>
  );
};

export default RegistryPage;
